import {reformatLocale} from 'app/utils/locale/reformatLocale';
import {type FormatDateParam, type FormatPlaceholder} from 'app/utils/template/interface';
import {baseTemplateMethods} from 'app/utils/template/template';

import {type GemSettings} from '../config/interface';

import {CustomMustache} from './customMustache';

export const getDateTemplateMethods = (gemSettings: GemSettings) => ({
    ...baseTemplateMethods,

    /**
     * Used for TWI, form text, form labels.
     * Usage: {{#now}}MM{{/now}}
     * @see https://jira.s-mxs.net/browse/EMIL-10372
     */
    now: () => {
        return (format: FormatPlaceholder) => {
            return formatDate(new Date(), format, gemSettings);
        };
    },
    /**
     * Used for TWI.
     * Usage: {{#formatDate}}{"date": {{ dateTimeFromApiResponse }},"format":"yyyy"}{{/formatDate}}
     * @see https://jira.s-mxs.net/browse/EMIL-10372
     */
    formatDate: () => {
        return (param: string, render: (value: string) => string) => {
            try {
                const {date, format} = JSON.parse(param) as FormatDateParam;
                return formatDate(new Date(render(date)), format, gemSettings);
            } catch (e) {
                console.error(e);
            }
        };
    },
});

function formatDate(date: Date, format: FormatPlaceholder, gemSettings: GemSettings) {
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    };

    switch (format) {
        case 'MMMM':
            options.month = 'long';
            break;
        case 'MM':
            options.month = '2-digit';
            break;
        case 'dd':
            options.day = '2-digit';
            break;
        case 'yy':
            options.year = '2-digit';
            break;
    }

    const dateTimeFormat = new Intl.DateTimeFormat(reformatLocale(gemSettings.language), options);
    const parts = dateTimeFormat.formatToParts(date);

    switch (format) {
        case 'yy':
        case 'yyyy':
            return parts.find((item) => item.type === 'year')?.value;
        case 'M':
        case 'MM':
        case 'MMMM':
            return parts.find((item) => item.type === 'month')?.value;
        case 'd':
        case 'dd':
            return parts.find((item) => item.type === 'day')?.value;
    }

    return dateTimeFormat.format(date).replace(/\s/g, '');
}

/**
 * Render template with given variables
 * @param templateInput
 * @param values
 * @param gemSettings
 * @param tags
 */
export function templateDate(templateInput: string, values: Record<string, any> | string, gemSettings: GemSettings, tags?: [string, string]): string {
    const normalizedValues = typeof values === 'string' ? {} : values;
    return CustomMustache.render(templateInput, {...normalizedValues, ...getDateTemplateMethods(gemSettings)}, {}, tags);
}
